import { useMemo, useState } from "react";

import { useLocalization } from "@fluent/react";
import { Chip, Popover, TextInput } from "@narmi/design_system";

import styles from "./SavedViewsDropdown.module.scss";

import type { AdvancedExportSavedView } from "../../hooks/useSavedViews";

interface SavedViewsDropdownProps {
  views: AdvancedExportSavedView[];
  isLoading: boolean;
  selectedView: AdvancedExportSavedView | null;
  onSelectView: (viewId: string) => void;
}

const SavedViewsDropdown = ({
  views,
  isLoading,
  selectedView,
  onSelectView,
}: SavedViewsDropdownProps) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredViews = useMemo(() => {
    if (!searchQuery.trim()) return views;
    const query = searchQuery.trim().toLowerCase();
    return views.filter((view) => view.name.toLowerCase().includes(query));
  }, [views, searchQuery]);

  const handleOpen = () => {
    setSearchQuery("");
    setIsOpen(true);
  };

  const handleDismiss = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleSelectView = (viewId: string) => {
    onSelectView(viewId);
    setIsOpen(false);
    setSearchQuery("");
  };

  const chipLabel = selectedView
    ? selectedView.name
    : l10n.getString("transaction-export-saved-views-label");

  const hasNoViews = !isLoading && views.length === 0;
  const hasNoSearchResults =
    !isLoading && views.length > 0 && filteredViews.length === 0;

  // Keeps focus inside the NDS Popover's FocusLock on click, or it scrolls back to the last focused checkbox.
  const popoverContent = (
    <div className={styles["saved-views-dropdown"]} tabIndex={-1}>
      <div className={styles["saved-views-dropdown-search"]}>
        <TextInput
          label={l10n.getString("transaction-export-saved-views-search")}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchQuery(e.target.value)
          }
          testId="saved-views-search"
        />
      </div>
      <div className={styles["saved-views-dropdown-divider"]} />
      <div className={styles["saved-views-dropdown-list"]}>
        {isLoading && (
          <div
            className={styles["saved-views-dropdown-loading"]}
            data-testid="saved-views-loading"
          >
            {l10n.getString("transaction-export-saved-views-loading")}
          </div>
        )}
        {hasNoViews && (
          <div
            className={styles["saved-views-dropdown-empty"]}
            data-testid="saved-views-empty"
          >
            {l10n.getString("transaction-export-saved-views-empty")}
          </div>
        )}
        {hasNoSearchResults && (
          <div
            className={styles["saved-views-dropdown-empty"]}
            data-testid="saved-views-no-results"
          >
            {l10n.getString("transaction-export-saved-views-no-results")}
          </div>
        )}
        {!isLoading &&
          filteredViews.map((view) => (
            <div
              key={view.id}
              className={styles["saved-views-dropdown-item"]}
              data-testid={`saved-view-${view.id}`}
              onClick={() => handleSelectView(view.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelectView(view.id);
                }
              }}
            >
              {view.view_type === "narmi_preset" && (
                <span data-testid="saved-view-preset-icon" aria-hidden="true">
                  ✦
                </span>
              )}
              <span className={styles["saved-views-dropdown-item-label"]}>
                {view.name}
              </span>
            </div>
          ))}
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      isOpen={isOpen}
      onUserDismiss={handleDismiss}
      testId="saved-views-popover"
    >
      <Chip
        kind="secondary"
        label={chipLabel}
        startIcon="bookmark"
        onClick={handleOpen}
        endIcon={`chevron-${isOpen ? "up" : "down"}`}
      />
    </Popover>
  );
};

export default SavedViewsDropdown;
